import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { CreditCard, Loader2, ChevronDown, TrendingDown, TrendingUp, PencilLine, Calendar as CalendarIcon, X } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CURRENCY_SYMBOLS = { GBP: "£", EUR: "€", USD: "$", CAD: "$", AUD: "$" };
const CURRENCY_LOCALES = { GBP: "en-GB", EUR: "en-IE", USD: "en-US", CAD: "en-CA", AUD: "en-AU" };

const formatHome = (amount, ccy) => {
  if (amount == null || !Number.isFinite(amount)) return "";
  try {
    return new Intl.NumberFormat(CURRENCY_LOCALES[ccy] || "en-GB", {
      style: "currency",
      currency: ccy,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${CURRENCY_SYMBOLS[ccy] || ""}${amount.toFixed(2)}`;
  }
};

const Field = ({ label, htmlFor, children, hint }) => (
  <div className="space-y-1.5">
    <label
      htmlFor={htmlFor}
      className="block text-[11px] uppercase tracking-[0.18em] text-slate-400 font-semibold"
    >
      {label}
    </label>
    {children}
    {hint && <p className="text-xs text-slate-500">{hint}</p>}
  </div>
);

const inputCls =
  "w-full rounded-lg bg-[#060B19] border border-[#1A2849] px-3.5 py-2.5 text-sm text-white placeholder-slate-600 focus:outline-none focus:border-[#00E676] focus:ring-2 focus:ring-[#00E676]/20 transition-colors";

// Convert "yyyy-MM-dd" ↔ local Date, avoiding UTC-vs-local shift.
const parseISODateLocal = (iso) => {
  if (!iso || typeof iso !== "string") return undefined;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  return new Date(y, m - 1, d);
};
const toISODateLocal = (d) => {
  if (!(d instanceof Date) || isNaN(d.getTime())) return "";
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const formatDueUK = (iso) => {
  const d = parseISODateLocal(iso);
  if (!d) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${d.getFullYear()}`;
};

const ManualCardDialog = ({ open, onOpenChange, onCreated, onSubmitted, defaultCurrency = "GBP", mode = "create", initial = null, onSaved }) => {
  const [name, setName] = useState(initial?.name || "");
  const [balance, setBalance] = useState(initial != null && initial.balance !== undefined ? String(initial.balance) : "");
  const [limit, setLimit] = useState(initial != null && initial.credit_limit !== undefined ? String(initial.credit_limit) : "");
  const [dueDate, setDueDate] = useState(initial?.due_date || "");
  const [currency, setCurrency] = useState((initial?.currency || defaultCurrency || "GBP").toUpperCase());
  const [lastFour, setLastFour] = useState(initial?.last_four || "");
  const [nickname, setNickname] = useState(initial?.nickname || "");
  const [defaultCategory, setDefaultCategory] = useState(initial?.default_category || "");
  const [monthlyCap, setMonthlyCap] = useState(
    initial?.monthly_cap != null ? String(initial.monthly_cap) : ""
  );
  const [saving, setSaving] = useState(false);
  const [fxRate, setFxRate] = useState(null); // rate: home per 1 selected
  // Edit-mode action selector: 'payment' | 'expense' | 'balance'
  const [editAction, setEditAction] = useState("payment");
  const [deltaAmount, setDeltaAmount] = useState("");
  // Expense-only: user-picked merchant category. Maps to CATEGORIES keys in
  // /app/frontend/src/lib/merchants.js so the badge + Category Breakdown
  // widget can group manual expenses next to Plaid transactions.
  const [expenseCategory, setExpenseCategory] = useState("");

  const homeCurrency = (defaultCurrency || "GBP").toUpperCase();
  const isForeign = currency && currency !== homeCurrency;

  // Fetch FX rate whenever the selected currency changes (and it differs from home)
  React.useEffect(() => {
    if (!open || !isForeign) {
      setFxRate(null);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/fx/rates`, {
        params: { home: homeCurrency, currencies: currency },
        withCredentials: true,
      })
      .then((res) => {
        if (cancelled) return;
        const rate = res?.data?.rates?.[currency];
        setFxRate(typeof rate === "number" ? rate : null);
      })
      .catch(() => {
        if (!cancelled) setFxRate(null);
      });
    return () => {
      cancelled = true;
    };
  }, [currency, homeCurrency, isForeign, open]);

  const balanceHome = isForeign && fxRate && Number(balance) > 0 ? Number(balance) * fxRate : null;
  const limitHome = isForeign && fxRate && Number(limit) > 0 ? Number(limit) * fxRate : null;

  React.useEffect(() => {
    if (!open) return;
    setName(initial?.name || "");
    setBalance(initial != null && initial.balance !== undefined ? String(initial.balance) : "");
    setLimit(initial != null && initial.credit_limit !== undefined ? String(initial.credit_limit) : "");
    setDueDate(initial?.due_date || "");
    setCurrency((initial?.currency || defaultCurrency || "GBP").toUpperCase());
    setLastFour(initial?.last_four || "");
    setNickname(initial?.nickname || "");
    setDefaultCategory(initial?.default_category || "");
    setMonthlyCap(initial?.monthly_cap != null ? String(initial.monthly_cap) : "");
    setEditAction("payment");
    setDeltaAmount("");
    // Pre-select the card's default_category if it has one; otherwise show
    // the "Select Category…" placeholder and force the user to pick.
    setExpenseCategory(initial?.default_category || "");
  }, [open, initial, defaultCurrency]);

  const reset = () => {
    setName("");
    setBalance("");
    setLimit("");
    setDueDate("");
    setCurrency((defaultCurrency || "GBP").toUpperCase());
    setLastFour("");
    setNickname("");
    setDefaultCategory("");
    setMonthlyCap("");
  };

  const submit = async (e) => {
    e.preventDefault();

    // ===== EDIT MODE — 3 explicit actions =====
    if (mode === "edit" && initial?.id) {
      const currentBalance = Number(initial.balance) || 0;
      const ccy = (initial.currency || defaultCurrency || "GBP").toUpperCase();
      const label = initial.nickname || initial.name || "Card";

      // Add Payment / Add Expense — single amount input, delta applied to balance
      if (editAction === "payment" || editAction === "expense") {
        const amt = Number(deltaAmount);
        if (!Number.isFinite(amt) || amt <= 0) {
          toast.error("Enter an amount greater than 0");
          return;
        }
        const newBalance = editAction === "payment"
          ? Math.max(0, currentBalance - amt)
          : currentBalance + amt;
        if (editAction === "payment" && amt > currentBalance) {
          toast.error(`Payment is larger than the current balance (${formatHome(currentBalance, ccy)})`);
          return;
        }
        setSaving(true);
        try {
          const patch = { balance: newBalance };
          if (editAction === "expense") {
            patch.category = expenseCategory;
          }
          const res = await axios.patch(
            `${API}/cards/${initial.id}`,
            patch,
            { withCredentials: true }
          );
          const txnId = res.data.last_transaction_id;
          const actionLabel = editAction === "payment" ? "Payment" : "Expense";
          toast.success(`${actionLabel} of ${formatHome(amt, ccy)} logged for ${label}`, {
            duration: 10000,
            action: txnId ? {
              label: "Undo",
              onClick: async () => {
                try {
                  await axios.post(`${API}/cards/transactions/${txnId}/undo`, {}, { withCredentials: true });
                  toast.success(`Undone — ${label} restored to ${formatHome(currentBalance, ccy)}`);
                  onSaved && onSaved({ ...res.data, balance: currentBalance });
                } catch {
                  toast.error("Could not undo — please try again");
                }
              },
            } : undefined,
          });
          onOpenChange(false);
          onSaved && onSaved(res.data);
        } catch (err) {
          const detail = err?.response?.data?.detail;
          toast.error(typeof detail === "string" ? detail : "Could not log the transaction");
        } finally {
          setSaving(false);
        }
        return;
      }

      // Edit Balance — full-form correction (validation as before)
      if (!name.trim()) {
        toast.error("Card name is required");
        return;
      }
      const balNum = Number(balance);
      const limNum = Number(limit);
      if (!Number.isFinite(balNum) || balNum < 0) {
        toast.error("Balance must be zero or greater");
        return;
      }
      if (!Number.isFinite(limNum) || limNum <= 0) {
        toast.error("Credit limit must be greater than 0");
        return;
      }
      if (balNum > limNum) {
        toast.error("Balance is higher than the credit limit — double-check the numbers");
        return;
      }
      setSaving(true);
      try {
        const payload = {
          name: name.trim(),
          balance: balNum,
          credit_limit: limNum,
          due_date: dueDate || null,
          currency: (currency || defaultCurrency).toUpperCase(),
          last_four: lastFour.trim() || null,
          nickname: nickname.trim() || null,
          default_category: defaultCategory || null,
          monthly_cap: defaultCategory && monthlyCap ? Number(monthlyCap) : null,
          intent: "correction",
        };
        const res = await axios.patch(`${API}/cards/${initial.id}`, payload, { withCredentials: true });
        const txnId = res.data.last_transaction_id;
        const prevBalance = initial?.balance;
        if (txnId && prevBalance != null) {
          toast.success(`Correction logged for ${label}`, {
            duration: 10000,
            action: {
              label: "Undo",
              onClick: async () => {
                try {
                  await axios.post(`${API}/cards/transactions/${txnId}/undo`, {}, { withCredentials: true });
                  toast.success(`Restored ${label} to ${formatHome(Number(prevBalance), ccy)}`);
                  onSaved && onSaved({ ...res.data, balance: prevBalance });
                } catch {
                  toast.error("Could not undo — please try again");
                }
              },
            },
          });
        } else {
          toast.success(`Updated ${label}`);
        }
        onOpenChange(false);
        onSaved && onSaved(res.data);
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "string"
          ? detail
          : (detail && typeof detail === "object" && detail.message) || "Could not save the card";
        toast.error(msg);
      } finally {
        setSaving(false);
      }
      return;
    }

    // ===== CREATE MODE — unchanged =====
    if (!name.trim()) {
      toast.error("Card name is required");
      return;
    }
    const balNum = Number(balance);
    const limNum = Number(limit);
    if (!Number.isFinite(balNum) || balNum < 0) {
      toast.error("Balance must be zero or greater");
      return;
    }
    if (!Number.isFinite(limNum) || limNum <= 0) {
      toast.error("Credit limit must be greater than 0");
      return;
    }
    if (balNum > limNum) {
      toast.error("Balance is higher than the credit limit — double-check the numbers");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        balance: balNum,
        credit_limit: limNum,
        due_date: dueDate || null,
        currency: (currency || defaultCurrency).toUpperCase(),
        last_four: lastFour.trim() || null,
        nickname: nickname.trim() || null,
        default_category: defaultCategory || null,
        monthly_cap: defaultCategory && monthlyCap ? Number(monthlyCap) : null,
      };
      const res = await axios.post(`${API}/cards`, payload, { withCredentials: true });
      toast.success(`Added ${res.data.nickname || res.data.name}`);
      reset();
      onOpenChange(false);
      onCreated && onCreated(res.data);
      onSubmitted && onSubmitted(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : (detail && typeof detail === "object" && detail.message) || "Could not save the card";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#0E172C] border border-[#1A2849] text-white sm:max-w-lg rounded-2xl p-0 gap-0 overflow-hidden flex flex-col max-h-[92dvh]"
        data-testid="manual-card-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#00E676]/10 border border-[#00E676]/30 flex items-center justify-center">
              <CreditCard size={17} className="text-[#00E676]" />
            </div>
            <div>
              <DialogTitle
                className="text-white text-xl tracking-tight"
                style={{ fontFamily: "'Outfit', sans-serif" }}
              >
                {mode === "edit" ? "Edit credit card" : "Add a credit card manually"}
              </DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">
                {mode === "edit"
                  ? "Update the balance, credit limit, or next due date. Changes flow into every utilization & optimization metric."
                  : "Include cards Plaid can't reach. They count toward every utilization & optimization metric."}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form
          onSubmit={submit}
          className="flex flex-col overflow-hidden flex-1 min-h-0"
          data-testid="manual-card-form"
        >
          <div
            className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-6 pb-6 space-y-5"
            data-testid="manual-card-scroll"
          >
          {mode === "edit" && (
            <>
              {/* 3-mode action selector */}
              <div className="grid grid-cols-3 gap-2" role="tablist" data-testid="edit-action-tabs">
                {[
                  { key: "payment", label: "Add Payment", Icon: TrendingDown, color: "#00E676", desc: "Log a payment you made" },
                  { key: "expense", label: "Add Expense", Icon: TrendingUp, color: "#F87171", desc: "Log a new charge" },
                  { key: "balance", label: "Edit Details", Icon: PencilLine, color: "#38BDF8", desc: "Change name, limit, due date" },
                ].map((opt) => {
                  const active = editAction === opt.key;
                  const Icon = opt.Icon;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setEditAction(opt.key)}
                      data-testid={`edit-action-${opt.key}`}
                      className={`rounded-xl border px-2 py-3 text-center transition-all ${
                        active
                          ? "border-transparent shadow-lg"
                          : "border-[#1A2849] hover:border-white/20 bg-[#060B19]"
                      }`}
                      style={active ? { background: `${opt.color}14`, borderColor: `${opt.color}88` } : undefined}
                    >
                      <Icon size={16} className="mx-auto mb-1" style={{ color: active ? opt.color : "#94A3B8" }} />
                      <div
                        className="text-[11px] font-semibold uppercase tracking-[0.08em]"
                        style={{ color: active ? opt.color : "#CBD5E1" }}
                      >
                        {opt.label}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Card summary — shown in edit mode so the user knows what they're editing */}
              <div className="rounded-xl border border-[#1A2849] bg-[#060B19]/60 px-4 py-3 flex items-center justify-between">
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Editing</div>
                  <div className="text-sm text-white font-semibold truncate">
                    {initial?.nickname || initial?.name}
                    {initial?.last_four ? <span className="text-slate-400 font-normal"> · ••{initial.last_four}</span> : null}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Balance</div>
                  <div className="text-sm text-white font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {formatHome(Number(initial?.balance) || 0, (initial?.currency || defaultCurrency || "GBP").toUpperCase())}
                  </div>
                </div>
              </div>
            </>
          )}

          {/* PAYMENT / EXPENSE — single-amount form */}
          {mode === "edit" && (editAction === "payment" || editAction === "expense") ? (
            <>
              <Field
                label={editAction === "payment" ? "Amount paid" : "Amount spent"}
                htmlFor="mc-delta"
                hint={
                  editAction === "payment"
                    ? "We'll decrease the balance and log a Payment transaction dated today."
                    : "We'll increase the balance and log an Expense transaction dated today."
                }
              >
                <input
                  id="mc-delta"
                  type="number"
                  inputMode="decimal"
                  min="0.01"
                  step="0.01"
                  value={deltaAmount}
                  onChange={(e) => setDeltaAmount(e.target.value)}
                  placeholder="0.00"
                  className={inputCls + " text-lg font-semibold"}
                  data-testid={`manual-card-${editAction}-input`}
                  autoFocus
                />
              </Field>
              {editAction === "expense" && (
                <Field
                  label="Category"
                  htmlFor="mc-category"
                  hint="Shows up as a coloured tag in Transaction History and rolls into your monthly Where-your-money-goes breakdown."
                >
                  <div className="relative">
                    <select
                      id="mc-category"
                      value={expenseCategory}
                      onChange={(e) => setExpenseCategory(e.target.value)}
                      data-testid="manual-card-expense-category"
                      required
                      className={inputCls + " appearance-none pr-9 cursor-pointer"}
                    >
                      <option value="" disabled>Select category…</option>
                      <option value="transport">Transport</option>
                      <option value="food">Eating Out</option>
                      <option value="groceries">Groceries</option>
                      <option value="utilities">Bills</option>
                      <option value="other">Others</option>
                    </select>
                    <ChevronDown
                      size={14}
                      className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                  </div>
                </Field>
              )}
            </>
          ) : (
            <>
              <Field label="Card name" htmlFor="mc-name">
                <input
                  id="mc-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Chase Sapphire Reserve"
                  className={inputCls}
                  data-testid="manual-card-name-input"
                  autoFocus={mode !== "edit"}
                />
              </Field>
              <Field
                label="Nickname (optional)"
                htmlFor="mc-nickname"
                hint={`Friendly label shown across the app · ${nickname.length}/30`}
              >
                <input
                  id="mc-nickname"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value.slice(0, 30))}
                  maxLength={30}
                  placeholder="e.g. Amex Everyday"
                  className={inputCls}
                  data-testid="manual-card-nickname-input"
                />
              </Field>
              <Field
                label="Default category (optional)"
                htmlFor="mc-default-category"
                hint="Pre-selected when you Add an Expense on this card. Great for a Groceries-only card."
              >
                <div className="relative">
                  <select
                    id="mc-default-category"
                    value={defaultCategory}
                    onChange={(e) => setDefaultCategory(e.target.value)}
                    data-testid="manual-card-default-category"
                    className={inputCls + " appearance-none pr-9 cursor-pointer"}
                  >
                    <option value="">None</option>
                    <option value="transport">Transport</option>
                    <option value="food">Eating Out</option>
                    <option value="groceries">Groceries</option>
                    <option value="utilities">Bills</option>
                    <option value="other">Others</option>
                  </select>
                  <ChevronDown
                    size={14}
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                  />
                </div>
              </Field>
              {defaultCategory && (
                <Field
                  label={`Monthly cap (optional, ${currency})`}
                  htmlFor="mc-monthly-cap"
                  hint="Get an email nudge at 80% and 100% of this cap. Tracks expenses on this card only."
                >
                  <input
                    id="mc-monthly-cap"
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={monthlyCap}
                    onChange={(e) => setMonthlyCap(e.target.value)}
                    placeholder="e.g. 200.00"
                    className={inputCls}
                    data-testid="manual-card-monthly-cap"
                  />
                </Field>
              )}
          <div className="grid grid-cols-2 gap-4">
            <Field
              label="Current balance"
              htmlFor="mc-balance"
              hint={
                balanceHome != null
                  ? `≈ ${formatHome(balanceHome, homeCurrency)} in your home currency`
                  : "What you owe right now"
              }
            >
              <input
                id="mc-balance"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                placeholder="1250.00"
                className={inputCls}
                data-testid="manual-card-balance-input"
              />
            </Field>
            <Field
              label="Credit limit"
              htmlFor="mc-limit"
              hint={
                limitHome != null
                  ? `≈ ${formatHome(limitHome, homeCurrency)} in your home currency`
                  : "Total credit line"
              }
            >
              <input
                id="mc-limit"
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={limit}
                onChange={(e) => setLimit(e.target.value)}
                placeholder="10000.00"
                className={inputCls}
                data-testid="manual-card-limit-input"
              />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Next due date" htmlFor="mc-due">
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    id="mc-due"
                    type="button"
                    data-testid="manual-card-due-input"
                    className={`${inputCls} flex items-center justify-between text-left ${dueDate ? "text-white" : "text-slate-500"}`}
                  >
                    <span className="inline-flex items-center gap-2 truncate">
                      <CalendarIcon size={14} className="text-slate-400 flex-shrink-0" />
                      {dueDate ? formatDueUK(dueDate) : "Pick a date"}
                    </span>
                    {dueDate ? (
                      <X
                        size={14}
                        role="button"
                        aria-label="Clear due date"
                        data-testid="manual-card-due-clear"
                        className="text-slate-500 hover:text-white transition-colors flex-shrink-0"
                        onClick={(e) => { e.stopPropagation(); setDueDate(""); }}
                      />
                    ) : (
                      <ChevronDown size={14} className="text-slate-500 flex-shrink-0" />
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="bg-[#0E172C] border border-[#1A2849] text-white p-0 rounded-xl shadow-xl"
                  data-testid="manual-card-due-popover"
                >
                  <Calendar
                    mode="single"
                    selected={parseISODateLocal(dueDate)}
                    onSelect={(d) => setDueDate(toISODateLocal(d))}
                    weekStartsOn={1}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </Field>
            <Field label="Last 4 digits" htmlFor="mc-lastfour" hint="For the Card Vault visual">
              <input
                id="mc-lastfour"
                inputMode="numeric"
                value={lastFour}
                onChange={(e) => setLastFour(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="1234"
                className={inputCls + " tracking-[0.3em]"}
                data-testid="manual-card-lastfour-input"
              />
            </Field>
          </div>
          <div>
            <Field
              label="Select Currency"
              htmlFor="mc-currency"
              hint={
                isForeign && fxRate
                  ? `1 ${currency} ≈ ${formatHome(fxRate, homeCurrency)} · totals converted to ${homeCurrency}`
                  : "Tap to switch — used for aggregate totals & FX conversion"
              }
            >
              <div className="relative">
                <select
                  id="mc-currency"
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                  className={inputCls + " tracking-widest appearance-none cursor-pointer pr-10"}
                  data-testid="manual-card-currency-input"
                >
                  <option value="GBP">GBP (£) — British Pound</option>
                  <option value="EUR">EUR (€) — Euro</option>
                  <option value="USD">USD ($) — US Dollar</option>
                  <option value="CAD">CAD ($) — Canadian Dollar</option>
                  <option value="AUD">AUD ($) — Australian Dollar</option>
                  {currency && !["GBP", "EUR", "USD", "CAD", "AUD"].includes(currency) && (
                    <option value={currency}>{currency}</option>
                  )}
                </select>
                <ChevronDown
                  size={16}
                  className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#00E676]"
                  data-testid="manual-card-currency-chevron"
                />
              </div>
            </Field>
          </div>
            </>
          )}

          </div>

          <DialogFooter
            className="shrink-0 border-t border-white/5 bg-[#0E172C] px-6 pt-3 pb-[max(1rem,env(safe-area-inset-bottom))] flex-row-reverse gap-2 sm:justify-start"
            data-testid="manual-card-footer"
          >
            <button
              type="submit"
              disabled={saving || (editAction === "expense" && !expenseCategory)}
              data-testid="manual-card-submit"
              className="inline-flex items-center gap-2 rounded-full bg-[#00E676] hover:bg-[#00C863] text-[#060B19] font-semibold px-6 py-2.5 text-xs transition-[transform,background-color] duration-200 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {saving && <Loader2 size={13} className="animate-spin" />}
              {saving
                ? "Saving…"
                : mode === "edit"
                ? editAction === "payment"
                  ? "Log payment"
                  : editAction === "expense"
                  ? "Log expense"
                  : "Save changes"
                : "Save credit card"}
            </button>
            <button
              type="button"
              onClick={() => onOpenChange(false)}
              disabled={saving}
              className="rounded-full border border-white/10 hover:border-white/30 px-5 py-2.5 text-xs text-slate-300 hover:text-white transition-colors"
              data-testid="manual-card-cancel"
            >
              Cancel
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ManualCardDialog;
